import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * One job, drawn end to end (KAN-1114).
 *
 * The landing's other sections each sell one capability; this one sells the
 * thing no single capability names — that the price, the order, the work, the
 * stock, the label and the invoice are readings of ONE record, so nothing is
 * retyped from one program into the next. A list of modules cannot say that,
 * because a list looks the same whether the parts are joined or not. A rail
 * with the steps on it says it at a glance.
 *
 * The bead riding the rail — and the part, box and vehicle it turns into on
 * the way out — is the „Forgmatic Platforma v6" design's (KAN-1329). It is
 * decoration: hidden below 1081 px and under ``prefers-reduced-motion``.
 */
const STEP_KEYS = [
  "drawing",
  "price",
  "order",
  "production",
  "stock",
  "label",
  "invoice",
] as const;

const ICO = {
  car: '<svg viewBox="0 0 24 24"><path d="M3 16v-2.4l1.8-4.1A2 2 0 0 1 6.6 8h8.8a2 2 0 0 1 1.8 1.1L19.4 13l1.6.6V16h-2"/><path d="M5 16h1"/><path d="M9.5 16h5"/><circle cx="7.5" cy="16.4" r="1.9"/><circle cx="16.5" cy="16.4" r="1.9"/><path d="M5.4 12.6h12"/></svg>',
  van: '<svg viewBox="0 0 24 24"><path d="M2 16V7h11v9"/><path d="M13 10h4.2l3.3 3.4V16h-1.6"/><path d="M4.6 16h1.2"/><path d="M10.6 16h4.8"/><circle cx="8" cy="16.4" r="1.9"/><circle cx="17.4" cy="16.4" r="1.9"/></svg>',
  truck: '<svg viewBox="0 0 24 24"><path d="M2 16V6h10v10"/><path d="M12 9h4.6l3.9 3.8V16h-1.4"/><path d="M4.4 16h1.2"/><path d="M9.6 16h4.6"/><circle cx="7.8" cy="16.5" r="1.9"/><circle cx="16.8" cy="16.5" r="1.9"/><path d="M14 12.6h5"/></svg>',
  train: '<svg viewBox="0 0 24 24"><rect x="5" y="4" width="14" height="11" rx="3"/><path d="M8 8h8"/><path d="M12 4v4"/><circle cx="8.6" cy="16.6" r="1.6"/><circle cx="15.4" cy="16.6" r="1.6"/><path d="M4 19h16"/></svg>',
  plane: '<svg viewBox="0 0 24 24"><path d="M21.5 3 11 13.5"/><path d="M21.5 3 14.8 21l-3.6-7.4L4 10z"/></svg>',
  sub: '<svg viewBox="0 0 24 24"><g transform="translate(24,0) scale(-1,1)"><path d="M3.5 13c0-2.2 3-4 7-4s6.5 1.8 6.5 4-2.5 4-6.5 4-7-1.8-7-4z"/><path d="M9.5 9V6.8h3V9"/><path d="M11 6.8V4.5"/><circle cx="7.6" cy="13" r="1.1"/><path d="M17 13h3"/><path d="M20 10.6v4.8"/></g></svg>',
  part: '<svg viewBox="0 0 24 24"><rect x="3.2" y="7.5" width="17.6" height="9" rx="2.2"/><circle cx="7.6" cy="12" r="1.5"/><circle cx="16.4" cy="12" r="1.5"/><path d="M11 7.5v9"/></svg>',
  boxOpen: '<svg viewBox="0 0 24 24"><path d="M12 12.6 3.6 8.5 12 4.6l8.4 3.9z"/><path d="M3.6 8.5v7.9L12 21.4v-8.8"/><path d="M20.4 8.5v7.9L12 21.4"/><path d="M3.6 8.5 8.1 3.2 12 4.6"/><path d="M20.4 8.5 15.9 3.2 12 4.6"/><path d="M12 12.6 6.9 12.1 3.6 8.5"/><path d="M12 12.6l5.1-.5 3.3-3.6"/></svg>',
  boxShut: '<svg viewBox="0 0 24 24"><path d="M12 2.9 21.1 7.5 12 12.1 2.9 7.5z"/><path d="M2.9 7.5v9.1L12 21.2v-9.1"/><path d="M21.1 7.5v9.1L12 21.2"/><path d="M7.3 5.2 16.5 9.8"/><path d="M16.5 9.8v3.1"/><path d="M9.3 10.2v3.1"/></svg>',
};

const FLEET = [
  { icon: ICO.car, dy: 0, rot: 0 },
  { icon: ICO.train, dy: 0, rot: 0 },
  { icon: ICO.plane, dy: -115, rot: -12 },
  { icon: ICO.van, dy: 0, rot: 0 },
  { icon: ICO.sub, dy: 115, rot: 10 },
  { icon: ICO.truck, dy: 0, rot: 0 },
];

/** What the bead carries on each leg: nothing until production, then the part. */
const CARGO = [null, null, null, ICO.part, ICO.boxOpen, ICO.boxShut];
const LEG_MS = 620;
const BEAD = '<span class="bead"></span>';

function useRunner(
  listRef: React.RefObject<HTMLOListElement>,
  runnerRef: React.RefObject<HTMLSpanElement>,
) {
  useEffect(() => {
    const list = listRef.current;
    const runner = runnerRef.current;
    if (!list || !runner) return;
    if (
      typeof window.matchMedia !== "function" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    let timers: number[] = [];
    const clearAll = () => {
      timers.forEach((id) => window.clearTimeout(id));
      timers = [];
    };
    const later = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(fn, ms));
    };
    const setCargo = (icon: string | null) => {
      runner.className = icon ? "tl-runner veh" : "tl-runner";
      runner.innerHTML = icon ?? BEAD;
    };

    const ride = () => {
      clearAll();
      if (window.innerWidth < 1081) {
        later(ride, 3000);
        return;
      }
      const base = list.getBoundingClientRect().left;
      const xs = Array.from(list.querySelectorAll<HTMLElement>(".tl-dot")).map(
        (dot) => {
          const r = dot.getBoundingClientRect();
          return r.left - base + r.width / 2 - 14;
        },
      );
      if (xs.length < 2) return;
      const end = xs[xs.length - 1];

      setCargo(null);
      runner.style.transition = "none";
      runner.style.transform = `translateX(${xs[0]}px)`;
      runner.style.opacity = "1";
      void runner.offsetWidth;
      for (let i = 1; i < xs.length; i++) {
        later(() => {
          setCargo(CARGO[i - 1]);
          runner.style.transition = `transform ${LEG_MS}ms linear`;
          runner.style.transform = `translateX(${xs[i]}px)`;
        }, (i - 1) * LEG_MS);
      }
      later(() => {
        const vehicle = FLEET[Math.floor(Math.random() * FLEET.length)];
        setCargo(vehicle.icon);
        runner.style.transition =
          "transform 1500ms cubic-bezier(0,0,.55,1),opacity .6s ease .85s";
        runner.style.transform = `translate(${end + 300}px,${vehicle.dy}px) rotate(${vehicle.rot}deg)`;
        runner.style.opacity = "0";
      }, (xs.length - 1) * LEG_MS);
      later(ride, (xs.length - 1) * LEG_MS + 2600);
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") ride();
    };
    ride();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearAll();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [listRef, runnerRef]);
}

export function PlatformJourney(): JSX.Element {
  const { t } = useTranslation("common");
  const listRef = useRef<HTMLOListElement>(null);
  const runnerRef = useRef<HTMLSpanElement>(null);
  useRunner(listRef, runnerRef);

  return (
    <section className="section alt" data-testid="platform-journey">
      <div className="inner">
        <p className="kicker">{t("platformLanding.journey.kicker")}</p>
        <h2 className="h2" style={{ maxWidth: 700 }}>
          {t("platformLanding.journey.heading")}
        </h2>
        <p className="lead" style={{ margin: "24px 0 0", maxWidth: 640 }}>
          {t("platformLanding.journey.lead")}
        </p>

        <ol className="tl" ref={listRef}>
          {/* Left empty on purpose: its content is written by ``useRunner``
              (bead, part, box, vehicle), never by React — so a re-render has
              no children of its own to reconcile against the ones the effect
              swapped in. */}
          <span aria-hidden="true" className="tl-runner" ref={runnerRef} />
          {STEP_KEYS.map((key, i) => (
            <li key={key} className="tl-node">
              <span aria-hidden="true" className="tl-dot">
                {i + 1}
              </span>
              <h3>{t(`platformLanding.journey.steps.${key}.title`)}</h3>
              <p>{t(`platformLanding.journey.steps.${key}.body`)}</p>
            </li>
          ))}
        </ol>

        <p className="tl-note">{t("platformLanding.journey.footnote")}</p>
      </div>
    </section>
  );
}
